import React from 'react';
import { WebsiteDesignProps } from './types';
import { Feather, Hourglass, Book, Moon } from 'lucide-react';
import { Button } from "@/components/ui/button";

const DarkAcademia: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Blackwood Archive";

    return (
        <div className="bg-[#1c1c1c] text-[#d4c5b0] min-h-screen font-serif selection:bg-[#3e2723] selection:text-[#d4c5b0]">
            {/* Grain Texture */}
            <div className="fixed inset-0 pointer-events-none opacity-5" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opactiy='0.5'/%3E%3C/svg%3E")` }}></div>

            <header className="border-b border-[#3e2723] py-8 relative z-10 bg-[#1c1c1c]/90">
                <div className="container mx-auto px-8 flex justify-between items-center">
                    <div className="text-3xl tracking-widest uppercase font-bold text-[#bcaaa4] flex items-center gap-3">
                         <Moon size={24} className="fill-[#bcaaa4]" /> {schoolName}
                    </div>
                    <nav className="hidden md:flex gap-8 text-sm uppercase tracking-widest text-[#8d6e63]">
                         <a href="#" className="hover:text-[#d4c5b0] transition-colors">The Canon</a>
                         <a href="#" className="hover:text-[#d4c5b0] transition-colors">Societies</a>
                         <a href="#" className="hover:text-[#d4c5b0] transition-colors">Library</a>
                         <a href="#" className="hover:text-[#d4c5b0] transition-colors">Enrol</a>
                    </nav>
                </div>
            </header>

            <main className="relative z-10">
                <section className="container mx-auto px-8 py-24 flex flex-col items-center text-center">
                    <span className="text-[#8d6e63] italic text-lg mb-6">— Est. 1888 —</span>
                    <h1 className="text-6xl md:text-8xl mb-8 font-medium leading-[0.9] text-[#eefeeb] mix-blend-screen opacity-90">
                        The Pursuit of <br/> Hidden Truths
                    </h1>
                    <p className="max-w-xl text-lg text-[#a1887f] leading-relaxed mb-12">
                        Within these halls, we whisper poetry to the silence and extract wisdom from the shadows. A classical education for the modern mystic.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-5xl">
                         <div className="bg-[#262626] border border-[#3e2723] p-8 rounded-sm hover:border-[#8d6e63] transition-colors group cursor-pointer">
                             <Feather className="w-8 h-8 mb-4 text-[#8d6e63] group-hover:text-[#d4c5b0]" />
                             <h3 className="text-xl mb-2 text-[#d4c5b0]">Literature</h3>
                             <p className="text-sm text-[#8d6e63]">Study the masters who dared to dream in ink.</p>
                         </div>
                         <div className="bg-[#262626] border border-[#3e2723] p-8 rounded-sm hover:border-[#8d6e63] transition-colors group cursor-pointer transform -translate-y-4 md:-translate-y-8">
                             <Hourglass className="w-8 h-8 mb-4 text-[#8d6e63] group-hover:text-[#d4c5b0]" />
                             <h3 className="text-xl mb-2 text-[#d4c5b0]">History</h3>
                             <p className="text-sm text-[#8d6e63]">Understand the cyclical nature of time.</p>
                         </div>
                         <div className="bg-[#262626] border border-[#3e2723] p-8 rounded-sm hover:border-[#8d6e63] transition-colors group cursor-pointer">
                             <Book className="w-8 h-8 mb-4 text-[#8d6e63] group-hover:text-[#d4c5b0]" />
                             <h3 className="text-xl mb-2 text-[#d4c5b0]">Philosophy</h3>
                             <p className="text-sm text-[#8d6e63]">Question the fabric of your reality.</p>
                         </div>
                    </div>
                </section>

                <section className="py-24 border-t border-[#3e2723] bg-[#1a1818]">
                    <div className="container mx-auto px-8 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                         <div className="relative">
                             <div className="absolute inset-0 bg-[#3e2723] transform translate-x-4 translate-y-4"></div>
                             <img src="https://images.unsplash.com/photo-1507842217343-583bb7260b66?q=80&w=2190&auto=format&fit=crop" className="relative w-full grayscale contrast-125 sepia-[.3]" alt="Library" />
                             <div className="absolute bottom-0 left-0 bg-[#1c1c1c] p-6 border-t border-r border-[#3e2723]">
                                 <p className="text-xs uppercase tracking-widest text-[#8d6e63]">The Grand Hall</p>
                             </div>
                         </div>
                         <div>
                             <h2 className="text-4xl italic mb-6 text-[#d4c5b0]">"I would always rather be happy than dignified."</h2>
                             <p className="text-[#a1887f] mb-8 leading-loose">
                                 Our community is small, tight-knit, and unapologetically obsessed with the arts. We offer boarding facilities that feel more like a private club than a dormitory.
                             </p>
                             <Button variant="outline" className="border-[#8d6e63] text-[#8d6e63] hover:bg-[#3e2723] hover:text-[#d4c5b0] uppercase tracking-widest text-xs">
                                 Request Invitation
                             </Button>
                         </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default DarkAcademia;
